/**
 * Locale-aware formatters. The current language is managed by the i18n layer
 * via setFormatLang() — call sites don't need to know about locale, they just
 * call arNumber/formatDate/relativeAr etc. and get the right output.
 */

const AR_DIGITS = ['٠','١','٢','٣','٤','٥','٦','٧','٨','٩'];

let currentLang = 'en';

export function setFormatLang(lang) {
  currentLang = lang === 'ar' ? 'ar' : 'en';
}

export function getFormatLang() {
  return currentLang;
}

export function toArabicDigits(input) {
  if (input === null || input === undefined) return '';
  return String(input).replace(/[0-9]/g, (d) => AR_DIGITS[+d]);
}

/**
 * Format a number for display in the current locale.
 *  - en: 12,345
 *  - ar: ١٢٬٣٤٥
 *
 * Name kept as `arNumber` for backwards-compatibility with existing call sites
 * across the codebase.
 */
export function arNumber(n) {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  if (currentLang === 'ar') {
    return toArabicDigits(Number(n).toLocaleString('en-US').replace(/,/g, '٬'));
  }
  return Number(n).toLocaleString('en-US');
}

export function arPercent(n) {
  if (n === null || n === undefined) return '—';
  const rounded = Math.round(n);
  return currentLang === 'ar'
    ? `${toArabicDigits(rounded)}٪`
    : `${rounded}%`;
}

/**
 * Relative time. Same name as before; in English mode returns "3m ago" /
 * "2h ago" / "5d ago", in Arabic returns the localized form with Indic digits.
 */
export function relativeAr(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Math.max(0, Date.now() - then);
  const mins = Math.floor(diff / 60000);
  const hrs = Math.floor(mins / 60);
  const days = Math.floor(hrs / 24);
  const months = Math.floor(days / 30);

  if (currentLang === 'ar') {
    if (mins < 1) return 'الآن';
    if (mins < 60) return `قبل ${toArabicDigits(mins)} د`;
    if (hrs < 24) return `قبل ${toArabicDigits(hrs)} س`;
    if (days < 30) return `قبل ${toArabicDigits(days)} يومًا`;
    return `قبل ${toArabicDigits(months)} شهر`;
  }
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (hrs < 24) return `${hrs}h ago`;
  if (days < 30) return `${days}d ago`;
  return `${months}mo ago`;
}

export function initials(name) {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2);
  return (parts[0][0] || '') + (parts[1][0] || '');
}

export function toDateInputValue(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function dateInputToPbDate(value) {
  if (!value) return '';
  return `${value} 00:00:00.000Z`;
}

export function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const locale = currentLang === 'ar' ? 'ar-EG' : 'en-US';
  return d.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function daysFromNow(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const diff = d.getTime() - Date.now();
  return Math.round(diff / 86400000);
}
